import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../../../hooks/useAuthenticatedUserId'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { reverseUrl } from '../../../../../navigation/reverseUrl'
import { theme } from '../../../../../theme/theme'
import { Button, ButtonVariant } from '../../../../common/Button'
import { ToastType } from '../../../../common/Toast/toast-type'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import { createRequest } from '../../../../../api/requests'
import { activateSociety } from '../../../../../api/societies'
import { i18nSocietyType } from '../../../../../i18n'

interface JoinInactiveSocietyBoardProps {
  society: ISocietiesModel
}

export const JoinInactiveSocietyBoard = ({
  society,
}: JoinInactiveSocietyBoardProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const navigate = useNavigate()
  const userId = useAuthenticatedUserId() as string

  const showErrorMessage = (message: string): void => {
    setToastNotification(ToastType.DANGER, message)
  }

  const showSuccessMessage = (message: string): void => {
    setToastNotification(ToastType.SUCCESS, message)
  }

  const navigateToSociety = (): void => {
    navigate(reverseUrl('society'))
  }

  const navigateToSocietyConfirmActivation = (): void => {
    navigate(
      reverseUrl('find-society:confirm-activation-detail', {
        id: society._id,
      }),
      { replace: true, state: { navigatingFromSocietyCreated: false } }
    )
  }

  const { mutate: mutateCreatRequest } = useMutation({
    mutationFn: createRequest,
    onSuccess: () => {
      showSuccessMessage(
        translate('inactiveSociety.flashMessage.joinSocietySuccess', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
      navigateToSociety()
    },
    onError: () => {
      showErrorMessage(
        translate('inactiveSociety.flashMessage.joinSocietyFailure', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    },
  })

  const onJoinClick = async (): Promise<void> => {
    mutateCreatRequest({
      societyId: society._id,
      userId,
    })
  }

  const { mutate: mutateActivateRequest } = useMutation({
    mutationFn: async (variables: { societyId: string; userId: string }) => {
      const response = (await createRequest(variables)).data.data
      if (!response) throw new Error()
      await activateSociety(society._id)
    },
    onSuccess: () => {
      showSuccessMessage(
        translate('inactiveSociety.flashMessage.activateSocietySuccess', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
      navigateToSocietyConfirmActivation()
    },
    onError: () => {
      showErrorMessage(
        translate('inactiveSociety.flashMessage.activateSocietyFailure', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    },
  })

  const onActivateClick = async (): Promise<void> => {
    mutateActivateRequest({
      societyId: society._id,
      userId,
    })
  }

  return (
    <div className="mb-4 mr-4 flex flex-col">
      <p className="mt-4" style={theme.textVariants.lead}>
        {translate('inactiveSociety.board.adminTitle')}
      </p>
      <p className="max-w-96 mt-4" style={theme.textVariants.base}>
        {translate('inactiveSociety.board.adminSubtitle', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
      <Button
        wrapperClassName="w-full"
        className="my-10 w-full"
        variant={ButtonVariant.PRIMARY}
        label={translate('common.actions.activate')}
        onClick={onActivateClick}
      />
      <div className="border-t" />
      <p className="mt-10" style={theme.textVariants.lead}>
        {translate('inactiveSociety.board.boardTitle')}
      </p>
      <p className="max-w-96 mt-4" style={theme.textVariants.base}>
        {translate('inactiveSociety.board.boardSubtitle', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
      <Button
        wrapperClassName="w-full"
        className="mt-10 w-full"
        variant={ButtonVariant.DEFAULT}
        label={translate('common.actions.join')}
        onClick={onJoinClick}
      />
    </div>
  )
}
